import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill, Input } from "../components/UI";
import { Vote, ShieldCheck, FileCheck2, TrendingUp, AlertTriangle } from "lucide-react";

export default function Political() {
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    (async () => {
      const [c, cl] = await Promise.all([api.get("/campaigns"), api.get("/clients")]);
      setCampaigns(c.data.filter(x => x.category === "Political")); setClients(cl.data);
    })();
  }, []);

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.business_name]));

  const POLICIES = [
    { platform: "Programmatic display", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "Connected TV", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "Online video", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "Audio / Podcast", allowed: true, verification: "Optional", disclaimer: "Required" },
    { platform: "DOOH", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "News publisher direct", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "Google / YouTube", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "Meta", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "X / Twitter", allowed: true, verification: "Required", disclaimer: "Required" },
    { platform: "TikTok", allowed: false, verification: "—", disclaimer: "—" },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Regulated Workflow"
        title="Political Intelligence"
        subtitle="Compliance-first political, public affairs, advocacy, and ballot-measure campaign workflows."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-medium">
            <Vote className="w-4 h-4 text-cyan-300" /> Active political campaigns
          </div>
          <div className="font-display text-3xl font-bold mt-2">{campaigns.length}</div>
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-medium">
            <FileCheck2 className="w-4 h-4 text-cyan-300" /> Awaiting compliance review
          </div>
          <div className="font-display text-3xl font-bold mt-2">{campaigns.filter(c => c.status === "Pending approval").length}</div>
        </Card>
        <Card>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 font-medium">
            <ShieldCheck className="w-4 h-4 text-emerald-300" /> Verified entities
          </div>
          <div className="font-display text-3xl font-bold mt-2">{clients.filter(c => c.is_political).length}</div>
        </Card>
      </div>

      <Card padded={false}>
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Political campaigns</h3>
          <Pill status="Pending">⚖ Compliance required</Pill>
        </div>
        {campaigns.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500">No political campaigns yet</div>
        ) : (
          <table className="w-full text-sm" data-testid="political-campaigns-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-5 py-3">Campaign</th>
                <th className="px-5 py-3">Entity</th>
                <th className="px-5 py-3">Disclaimer</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id} className="border-b border-white/5">
                  <td className="px-5 py-3 font-medium">{c.name}</td>
                  <td className="px-5 py-3 text-slate-300">{clientMap[c.client_id]}</td>
                  <td className="px-5 py-3 text-xs text-slate-400 truncate max-w-md">{c.political_disclaimer || <span className="text-rose-300">Missing</span>}</td>
                  <td className="px-5 py-3"><Pill status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-cyan-300" /> Platform policy guardrails
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="py-2 pr-4">Platform</th>
              <th className="py-2 pr-4">Political ads</th>
              <th className="py-2 pr-4">Verification</th>
              <th className="py-2 pr-4">Disclaimer</th>
            </tr>
          </thead>
          <tbody>
            {POLICIES.map(p => (
              <tr key={p.platform} className="border-b border-white/5">
                <td className="py-2 pr-4 text-white">{p.platform}</td>
                <td className="py-2 pr-4">{p.allowed ? <span className="text-emerald-300">Allowed</span> : <span className="text-rose-300">Blocked</span>}</td>
                <td className="py-2 pr-4 text-slate-300">{p.verification}</td>
                <td className="py-2 pr-4 text-slate-300">{p.disclaimer}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card>
        <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-300" /> Prohibited content & targeting
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-300">
          {[
            "False voting-process claims",
            "Voter-suppression messaging",
            "Deceptive synthetic media without disclosure",
            "Unauthorized impersonation",
            "Inferred race / ethnicity / religion targeting",
            "Health, union membership, or protected-trait targeting",
            "Raw device ID exports",
            "Unverified opposition claims without citation",
          ].map(s => <div key={s} className="flex gap-2"><span className="text-rose-300">×</span> {s}</div>)}
        </div>
      </Card>
    </div>
  );
}
